import { useEffect, useRef, useState } from 'react';
import { useAIChat } from '../context/AIChatContext';
import { useLanguage } from '../context/LanguageContext';
import { sendToGemini } from '../utils/gemini';
import { renderMessage } from '../utils/renderMessage';

function GeminiIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      <path d="M14 2C14 2 15.8 9.2 22 14C15.8 18.8 14 26 14 26C14 26 12.2 18.8 6 14C12.2 9.2 14 2 14 2Z" fill="currentColor" />
    </svg>
  );
}

function FloatingAIChat() {
  const { isOpen, setIsOpen } = useAIChat();
  const { isArabic } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  async function handleSend() {
    const text = input.trim();
    if (!text || loading) return;
    const next = [...messages, { role: 'user', text }];
    setMessages(next);
    setInput('');
    setLoading(true);
    try {
      const reply = await sendToGemini(next);
      setMessages((prev) => [...prev, { role: 'model', text: reply }]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'model',
          text: isArabic ? `حدث خطأ: ${e.message}` : `Error: ${e.message}`,
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  const dir = isArabic ? 'rtl' : 'ltr';

  return (
    <>
      {isOpen && (
        <div className="ai-chat-panel" dir={dir}>
          <div className="ai-chat-header">
            <div className="ai-chat-header-title">
              <GeminiIcon className="ai-chat-header-icon" />
              <span>{isArabic ? 'مساعد حلول الذكي' : 'Holol AI Assistant'}</span>
            </div>
            <button className="ai-chat-close" onClick={() => setIsOpen(false)} aria-label="Close chat">
              ✕
            </button>
          </div>

          <div className="ai-chat-messages">
            {messages.length === 0 && (
              <div className="ai-chat-welcome">
                <GeminiIcon className="ai-chat-welcome-icon" />
                <p>
                  {isArabic
                    ? 'مرحباً! أنا مساعد حلول الذكي. كيف يمكنني مساعدتك اليوم؟'
                    : "Hello! I'm Holol AI Assistant. How can I help you today?"}
                </p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`ai-chat-bubble ai-chat-bubble-${m.role}`}>
                {renderMessage(m.text)}
              </div>
            ))}
            {loading && (
              <div className="ai-chat-bubble ai-chat-bubble-model ai-chat-typing">
                <span /><span /><span />
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="ai-chat-footer" dir={dir}>
            <input
              ref={inputRef}
              className="ai-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder={isArabic ? 'اكتب رسالتك…' : 'Type a message…'}
              disabled={loading}
              dir={dir}
            />
            <button
              className="ai-chat-send-btn"
              onClick={handleSend}
              disabled={loading || !input.trim()}
              aria-label="Send"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <button
        className={`floating-ai${isOpen ? ' floating-ai-active' : ''}`}
        onClick={() => setIsOpen((v) => !v)}
        aria-label={isArabic ? 'مساعد حلول الذكي' : 'AI Assistant'}
      >
        <GeminiIcon className="floating-ai-icon" />
      </button>
    </>
  );
}

export default FloatingAIChat;
